import React, { useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  FileDown,
  Filter,
  ArrowLeft,
  ChevronRight,
  TrendingUp,
  Ship,
  Anchor,
  BarChart3,
  Download,
  Calendar,
} from "lucide-react";
import "./styles.css";
import MaritimeLayout from "../common/Applayout";

export default function RevenueMeteringDashboard() {
  const [selectedEmirate, setSelectedEmirate] = useState(null);
  const [selectedPort, setSelectedPort] = useState(null);
  const [selectedTerminal, setSelectedTerminal] = useState(null);

  const emirateSummary = [
    {
      emirate: "Dubai",
      ports: 1,
      events: 1290000,
      gross: 483750000,
      color: "#2563eb",
      growth: "+12.5%",
    },
    {
      emirate: "Abu Dhabi",
      ports: 1,
      events: 409000,
      gross: 153375000,
      color: "#1d4ed8",
      growth: "+8.3%",
    },
    {
      emirate: "Sharjah",
      ports: 1,
      events: 50000,
      gross: 2250000,
      color: "#3b82f6",
      growth: "+5.7%",
    },
    {
      emirate: "Fujairah",
      ports: 1,
      events: 800,
      gross: 432000,
      color: "#60a5fa",
      growth: "+15.2%",
    },
    {
      emirate: "RAK",
      ports: 1,
      events: 15000,
      gross: 540000,
      color: "#93c5fd",
      growth: "+3.4%",
    },
    {
      emirate: "Ajman",
      ports: 1,
      events: 12000,
      gross: 420000,
      color: "#a5b4fc",
      growth: "+7.8%",
    },
    {
      emirate: "UAQ",
      ports: 1,
      events: 8000,
      gross: 300000,
      color: "#bfdbfe",
      growth: "+2.1%",
    },
  ];

  const portSummary = {
    Dubai: [
      {
        port: "Jebel Ali",
        terminals: 2,
        events: 1290000,
        gross: 483750000,
        services: 12,
        efficiency: "98%",
        vessels: 2450,
      },
    ],
    "Abu Dhabi": [
      {
        port: "Khalifa",
        terminals: 1,
        events: 409000,
        gross: 153375000,
        services: 8,
        efficiency: "96%",
        vessels: 1200,
      },
    ],
    Sharjah: [
      {
        port: "Sharjah KCT",
        terminals: 1,
        events: 50000,
        gross: 2250000,
        services: 5,
        efficiency: "94%",
        vessels: 450,
      },
    ],
    Fujairah: [
      {
        port: "Fujairah Port",
        terminals: 1,
        events: 800,
        gross: 432000,
        services: 3,
        efficiency: "92%",
        vessels: 85,
      },
    ],
  };

  const terminalDetails = {
    "Jebel Ali": [
      {
        terminal: "T1",
        service: "TEU Handling",
        qty: 500000,
        fee: 375,
        gross: 187500000,
        utilization: "87%",
        vessels: 1200,
      },
      {
        terminal: "T2",
        service: "Bulk Cargo",
        qty: 790000,
        fee: 375,
        gross: 296250000,
        utilization: "92%",
        vessels: 1250,
      },
    ],
    Khalifa: [
      {
        terminal: "East Hub",
        service: "Container Ops",
        qty: 409000,
        fee: 375,
        gross: 153375000,
        utilization: "85%",
        vessels: 1200,
      },
    ],
    "Sharjah KCT": [
      {
        terminal: "KCT-1",
        service: "Bulk Cargo",
        qty: 50000,
        fee: 45,
        gross: 2250000,
        utilization: "78%",
        vessels: 450,
      },
    ],
    "Fujairah Port": [
      {
        terminal: "FP-1",
        service: "Pilotage",
        qty: 800,
        fee: 540,
        gross: 432000,
        utilization: "82%",
        vessels: 85,
      },
    ],
  };

  const monthlyTrends = [
    { month: "Jan", gross: 200, target: 180 },
    { month: "Feb", gross: 220, target: 190 },
    { month: "Mar", gross: 250, target: 210 },
    { month: "Apr", gross: 280, target: 240 },
    { month: "May", gross: 300, target: 260 },
    { month: "Jun", gross: 310, target: 280 },
    { month: "Jul", gross: 320, target: 300 },
    { month: "Aug", gross: 330, target: 310 },
    { month: "Sep", gross: 340, target: 320 },
    { month: "Oct", gross: 355, target: 330 },
    { month: "Nov", gross: 370, target: 350 },
  ];

  const serviceDistribution = [
    { name: "Container Ops", value: 65, color: "#2563eb" },
    { name: "Bulk Cargo", value: 20, color: "#1d4ed8" },
    { name: "Pilotage", value: 8, color: "#3b82f6" },
    { name: "Other Services", value: 7, color: "#60a5fa" },
  ];

  const exportData = (format) => {
    if (format === "csv") alert("Revenue Metering Data exported as CSV");
    else if (format === "pdf") alert("Revenue Metering Report exported as PDF");
  };

  // Custom Card Component
  const CustomCard = ({ children, className = "", onClick, hover = false }) => (
    <div
      className={`custom-card ${hover ? "cursor-pointer" : ""} ${className}`}
      onClick={onClick}
    >
      {children}
    </div>
  );

  const CustomCardHeader = ({ children, className = "" }) => (
    <div className={`custom-card-header ${className}`}>{children}</div>
  );

  const CustomCardTitle = ({ children, className = "" }) => (
    <h3 className={`custom-card-title ${className}`}>{children}</h3>
  );

  const CustomCardContent = ({ children, className = "" }) => (
    <div className={`custom-card-content ${className}`}>{children}</div>
  );

  if (selectedTerminal) {
    const details = terminalDetails[selectedTerminal] || [];
    return (
      <MaritimeLayout>
        {" "}
        <div className="revenue-dashboard">
          <div className="flex items-center mb-6">
            <button
              onClick={() => setSelectedTerminal(null)}
              className="btn btn-secondary"
            >
              <ArrowLeft className="mr-2" size={18} /> Back to Port
            </button>
            <div className="flex space-x-3" style={{ marginLeft: "auto" }}>
              <button
                onClick={() => exportData("csv")}
                className="btn btn-success"
              >
                <Download className="mr-2" size={16} /> Export CSV
              </button>
              <button
                onClick={() => exportData("pdf")}
                className="btn btn-primary"
              >
                <FileDown className="mr-2" size={16} /> Export PDF
              </button>
            </div>
          </div>

          <div className="dashboard-header">
            <div className="flex items-center mb-2">
              <div
                className="icon-container"
                style={{
                  background: "linear-gradient(to right, #3b82f6, #2563eb)",
                }}
              >
                <Ship className="text-white" size={24} />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-slate-800">
                  Terminal Revenue – {selectedTerminal}
                </h1>
                <p className="text-slate-600 mt-1">
                  Detailed breakdown of services and revenue streams
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg-grid-cols-2 gap-6">
            {details.map((t, i) => (
              <CustomCard key={i} className="border-slate-200">
                <CustomCardHeader>
                  <CustomCardTitle>
                    <div className="icon-sm bg-blue-100">
                      <Anchor className="text-blue-600" size={16} />
                    </div>
                    {t.terminal}
                  </CustomCardTitle>
                </CustomCardHeader>
                <CustomCardContent>
                  <div className="space-y-4">
                    <div className="data-grid grid-cols-2">
                      <div className="data-item">
                        <div className="data-label">Service Type</div>
                        <div className="data-value">{t.service}</div>
                      </div>
                      <div className="data-item">
                        <div className="data-label">Utilization</div>
                        <div className="data-value text-blue-600">
                          {t.utilization}
                        </div>
                      </div>
                    </div>

                    <div className="revenue-highlight">
                      <div className="flex justify-between items-center mb-2">
                        <span className="data-label">Quantity Processed</span>
                        <span className="data-value">
                          {t.qty.toLocaleString()}
                        </span>
                      </div>
                      <div className="flex justify-between items-center mb-2">
                        <span className="data-label">Unit Fee</span>
                        <span className="data-value">AED {t.fee}</span>
                      </div>
                      <div className="flex justify-between items-center pt-2 border-t border-blue-100">
                        <span className="data-label">Gross Revenue</span>
                        <span className="data-value-lg text-green-600">
                          AED {t.gross.toLocaleString()}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center justify-between text-sm text-slate-600">
                      <span>
                        Vessels Handled: <strong>{t.vessels}</strong>
                      </span>
                      <div className="efficiency-badge">
                        <TrendingUp size={14} className="mr-1" />
                        Optimal Performance
                      </div>
                    </div>
                  </div>
                </CustomCardContent>
              </CustomCard>
            ))}
          </div>
        </div>
      </MaritimeLayout>
    );
  }

  if (selectedPort) {
    const terminals = terminalDetails[selectedPort]
      ? Object.keys(terminalDetails).filter((k) => k === selectedPort)
      : [];
    return (
      <MaritimeLayout>
        {" "}
        <div className="revenue-dashboard">
          <div className="flex items-center mb-6">
            <button
              onClick={() => setSelectedPort(null)}
              className="btn btn-secondary"
            >
              <ArrowLeft className="mr-2" size={18} /> Back to Emirates
            </button>
          </div>

          <div className="dashboard-header">
            <div className="flex items-center mb-2">
              <div
                className="icon-container"
                style={{
                  background: "linear-gradient(to right, #10b981, #059669)",
                }}
              >
                <Anchor className="text-white" size={24} />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-slate-800">
                  Port Terminals – {selectedPort}
                </h1>
                <p className="text-slate-600 mt-1">
                  Select a terminal to view detailed revenue breakdown
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md-grid-cols-2 lg-grid-cols-3 gap-6">
            {terminals.map((t, i) => (
              <CustomCard
                key={i}
                hover={true}
                onClick={() => setSelectedTerminal(t)}
                className="border-slate-200"
              >
                <CustomCardHeader>
                  <CustomCardTitle>
                    <div className="icon-sm bg-green-100">
                      <Ship className="text-green-600" size={16} />
                    </div>
                    {t}
                  </CustomCardTitle>
                </CustomCardHeader>
                <CustomCardContent>
                  <div className="space-y-3">
                    <p className="text-slate-700 leading-relaxed">
                      <b>Click to view</b> terminal-level breakdowns of
                      services, quantities, and revenue metrics.
                    </p>
                    <div className="flex items-center justify-between pt-3 border-t border-slate-200">
                      <span className="text-sm text-slate-600">
                        Available Services
                      </span>
                      <div className="flex items-center text-blue-600 font-semibold">
                        View Details <ChevronRight size={16} className="mr-1" />
                      </div>
                    </div>
                  </div>
                </CustomCardContent>
              </CustomCard>
            ))}
          </div>
        </div>
      </MaritimeLayout>
    );
  }

  if (selectedEmirate) {
    const ports = portSummary[selectedEmirate] || [];
    return (
      <MaritimeLayout>
        {" "}
        <div className="revenue-dashboard">
          <div className="flex items-center mb-6">
            <button
              onClick={() => setSelectedEmirate(null)}
              className="btn btn-secondary"
            >
              <ArrowLeft className="mr-2" size={18} /> Back to Dashboard
            </button>
          </div>

          <div className="dashboard-header">
            <div className="flex items-center mb-2">
              <div
                className="icon-container"
                style={{
                  background: "linear-gradient(to right, #8b5cf6, #7c3aed)",
                }}
              >
                <Ship className="text-white" size={24} />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-slate-800">
                  Ports – {selectedEmirate}
                </h1>
                <p className="text-slate-600 mt-1">
                  Comprehensive port performance and revenue analytics
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md-grid-cols-2 lg-grid-cols-3 gap-6">
            {ports.map((p, i) => (
              <CustomCard
                key={i}
                hover={true}
                onClick={() => setSelectedPort(p.port)}
                className="border-slate-200"
              >
                <CustomCardHeader>
                  <CustomCardTitle>
                    <div className="icon-sm bg-purple-100">
                      <Anchor className="text-purple-600" size={16} />
                    </div>
                    {p.port}
                  </CustomCardTitle>
                </CustomCardHeader>
                <CustomCardContent>
                  <div className="space-y-4">
                    <div className="data-grid grid-cols-2">
                      <div className="data-item">
                        <div className="data-label">Terminals</div>
                        <div className="data-value">{p.terminals}</div>
                      </div>
                      <div className="data-item">
                        <div className="data-label">Services</div>
                        <div className="data-value">{p.services}</div>
                      </div>
                    </div>

                    <div
                      className="revenue-highlight"
                      style={{
                        background:
                          "linear-gradient(135deg, #f3e8ff 0%, #e9d5ff 100%)",
                        borderColor: "#ddd6fe",
                      }}
                    >
                      <div className="flex justify-between items-center mb-2">
                        <span className="data-label">Events</span>
                        <span className="data-value">
                          {p.events.toLocaleString()}
                        </span>
                      </div>
                      <div className="flex justify-between items-center pt-2 border-t border-purple-200">
                        <span className="data-label">Gross Revenue</span>
                        <span className="data-value-lg text-green-600">
                          AED {p.gross.toLocaleString()}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center justify-between text-sm">
                      <div className="efficiency-badge">
                        <TrendingUp size={14} className="mr-1" />
                        Efficiency: {p.efficiency}
                      </div>
                      <div className="flex items-center text-blue-600 font-semibold">
                        View Terminals{" "}
                        <ChevronRight size={14} className="mr-1" />
                      </div>
                    </div>
                  </div>
                </CustomCardContent>
              </CustomCard>
            ))}
          </div>
        </div>
      </MaritimeLayout>
    );
  }

  return (
    <MaritimeLayout>
      {" "}
      <div className="revenue-dashboard">
        {/* Header */}
        <div className="dashboard-header">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <div
                className="icon-container"
                style={{
                  background: "linear-gradient(to right, #2563eb, #7c3aed)",
                }}
              >
                <BarChart3 className="text-white" size={28} />
              </div>
              <div>
                <h1 className="gradient-text">National Maritime Center</h1>
                <p className="text-slate-600 mt-1 text-lg">
                  Revenue Metering Dashboard
                </p>
              </div>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => exportData("csv")}
                className="btn btn-success"
              >
                <Download className="mr-2" size={18} /> Export CSV
              </button>
              <button
                onClick={() => exportData("pdf")}
                className="btn btn-primary"
              >
                <FileDown className="mr-2" size={18} /> Export PDF
              </button>
            </div>
          </div>

          <p className="text-slate-600 text-lg leading-relaxed bg-blue-50 rounded-xl p-4 border border-blue-100">
            The Revenue Metering Dashboard provides detailed analytics on vessel
            movements, service transactions, and revenue generation across all
            UAE ports. Use filters to analyze data by Emirate, Port, Terminal,
            or Date Range. Click on a card to drill down further.
          </p>
        </div>

        {/* Filters */}
        <div className="filter-bar">
          <div className="filter-item">
            <Filter className="mr-2" size={16} /> Filter by Emirate
          </div>
          <div className="filter-item">
            <Filter className="mr-2" size={16} /> Filter by Port
          </div>
          <div className="filter-item">
            <Filter className="mr-2" size={16} /> Filter by Terminal
          </div>
          <div className="filter-item">
            <Calendar className="mr-2" size={16} /> Date Range: Nov 2025
          </div>
        </div>

        {/* Emirates Grid */}
        <div className="grid grid-cols-4 gap-6">
          {emirateSummary.map((e, i) => (
            <CustomCard
              key={i}
              hover={true}
              onClick={() => setSelectedEmirate(e.emirate)}
              className="border-slate-200"
            >
              <CustomCardHeader>
                <div className="flex items-center justify-between">
                  <CustomCardTitle>
                    <div
                      className="w-3 h-3 rounded-full mr-3"
                      style={{ backgroundColor: e.color }}
                    />
                    {e.emirate}
                  </CustomCardTitle>
                  <div className="growth-badge">
                    <TrendingUp size={14} className="mr-1" />
                    {e.growth}
                  </div>
                </div>
              </CustomCardHeader>
              <CustomCardContent>
                <div className="space-y-4">
                  <div className="data-grid grid-cols-2">
                    <div className="data-item">
                      <div className="data-label">Ports</div>
                      <div className="data-value">{e.ports}</div>
                    </div>
                    <div className="data-item">
                      <div className="data-label">Events</div>
                      <div className="data-value">
                        {e.events.toLocaleString()}
                      </div>
                    </div>
                  </div>

                  <div className="revenue-highlight revenue-success">
                    <div className="data-label">Gross Revenue</div>
                    <div className="data-value-lg text-green-600">
                      AED {e.gross.toLocaleString()}
                    </div>
                  </div>

                  <div className="flex items-center justify-between text-sm text-slate-600 pt-2 border-t border-slate-200">
                    <span>Click for details</span>
                    <ChevronRight size={16} />
                  </div>
                </div>
              </CustomCardContent>
            </CustomCard>
          ))}
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-2 gap-6">
          {/* Monthly Trend Chart */}
          <CustomCard className="border-slate-200">
            <CustomCardHeader>
              <CustomCardTitle>
                <div className="icon-sm bg-blue-100">
                  <TrendingUp className="text-blue-600" size={16} />
                </div>
                Monthly Revenue Trend (AED Millions)
              </CustomCardTitle>
            </CustomCardHeader>
            <CustomCardContent>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={monthlyTrends}>
                  <XAxis dataKey="month" stroke="#64748b" />
                  <YAxis stroke="#64748b" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "white",
                      border: "1px solid #e2e8f0",
                      borderRadius: "8px",
                      boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="gross"
                    stroke="#2563eb"
                    strokeWidth={3}
                    dot={{ fill: "#2563eb", strokeWidth: 2, r: 4 }}
                    activeDot={{ r: 6, fill: "#1d4ed8" }}
                  />
                  <Line
                    type="monotone"
                    dataKey="target"
                    stroke="#94a3b8"
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    dot={{ fill: "#94a3b8", r: 3 }}
                  />
                </LineChart>
              </ResponsiveContainer>
              <p className="text-sm text-slate-600 mt-4 flex items-center">
                <TrendingUp className="mr-2 text-green-600" size={16} />
                Shows monthly revenue growth trend across all Emirates with
                target comparison.
              </p>
            </CustomCardContent>
          </CustomCard>

          {/* Service Distribution Chart */}
          <CustomCard className="border-slate-200">
            <CustomCardHeader>
              <CustomCardTitle>
                <div className="icon-sm bg-green-100">
                  <BarChart3 className="text-green-600" size={16} />
                </div>
                Service Revenue Distribution
              </CustomCardTitle>
            </CustomCardHeader>
            <CustomCardContent>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={serviceDistribution}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {serviceDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "white",
                      border: "1px solid #e2e8f0",
                      borderRadius: "8px",
                      boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="data-grid grid-cols-2 mt-4">
                {serviceDistribution.map((service, index) => (
                  <div key={index} className="flex items-center text-sm">
                    <div
                      className="w-3 h-3 rounded-full mr-2"
                      style={{ backgroundColor: service.color }}
                    />
                    <span className="text-slate-700">
                      {service.name}: {service.value}%
                    </span>
                  </div>
                ))}
              </div>
            </CustomCardContent>
          </CustomCard>
        </div>

        {/* Bar Chart */}
        <CustomCard className="border-slate-200">
          <CustomCardHeader>
            <CustomCardTitle>
              <div className="icon-sm bg-purple-100">
                <Ship className="text-purple-600" size={16} />
              </div>
              Emirate-Wise Event Volume & Gross Revenue
            </CustomCardTitle>
          </CustomCardHeader>
          <CustomCardContent>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={emirateSummary}>
                <XAxis dataKey="emirate" stroke="#64748b" />
                <YAxis stroke="#64748b" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "white",
                    border: "1px solid #e2e8f0",
                    borderRadius: "8px",
                    boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
                  }}
                />
                <Bar dataKey="gross" fill="#2563eb" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
            <p className="text-sm text-slate-600 mt-4 flex items-center">
              <BarChart3 className="mr-2 text-blue-600" size={16} />
              Compares gross revenue contribution and event volume per Emirate
              with interactive tooltips.
            </p>
          </CustomCardContent>
        </CustomCard>
      </div>
    </MaritimeLayout>
  );
}
